<template>
<div class="margin_horizontal">
        <div class="pageTitle">
            <span class="md-title">{{ $t("management.accounts.title") }}</span>
        </div>
        <div>
            <div class="table_description table_description_filter">
                <span>{{$t('management.accounts.description')}}</span>
            </div>
            <div class="table_head table_head_filter">
                <md-button class="md-blank" @click="showAddAccountModal(true)">
                    <span class>+</span>
                    {{$t("management.accounts.add.button")}}
                </md-button>
            </div>
            <div class="flex filter_table">
                <div class="flex flex_column w_100">
                    <ag-item :rowData="rowData"
                             :columnDefs="columnDefs"
                             v-on:sortChanged="sortChanged"
                             :with-title="true"
                             :with-description="true"
                             :with-header="true"
                    />
                    <Pagination
                        :totalNum="totalNum"
                        :pageSize="pageSize"
                        :sizeChange="sizeChange"
                        :currentChange="currentChange"
                        :currentPage="page"
                    ></Pagination>
                </div>
                <BankAccountsFilter @apply-filter="applyFilter"/>
            </div>

            <AddBankAccountModal v-if="isDisplayAddAccountModal"
                                 @close="hideAddAccountModal"
                                 @added="onAccountAdded"
            ></AddBankAccountModal>
            <EditBankAccountModal v-if="isDisplayEditAccountModal"
                                  :account-data="editAccountData"
                                  @close="hideEditAccountModal"
                                  @changed="onAccountChanged"
            ></EditBankAccountModal>
        </div>
    </div>
</template>

<script>
import AgItem from "@/components/templates/agGrid/AgItem";
import EditBankAccountModal from "@/components/templates/bankAccount/EditBankAccountModal.vue";
import BankAccountsFilter from "@/components/templates/bankAccount/BankAccountsFilter.vue";

import {accountManagementApi} from "@/common/api/accountManagementApi";
import {enumDef} from "@/common/enumDefs";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import AddBankAccountModal from "@/components/templates/bankAccount/AddBankAccountModal";

export default {
    extends: PaginationParent,
    components: {
        AddBankAccountModal,
        AgItem,
        EditBankAccountModal,
        BankAccountsFilter,
        Pagination
    },
    data() {
        return {
            isDisplayAddAccountModal: false,
            isDisplayEditAccountModal: false,
            editAccountData: null,
            filters: [],
            sorting: [],
            currency: [],
            rowData: [],
            columnDefs: [
                {
                    field: "isMainAccount",
                    hide: true,
                    sortable: true,
                    sort: "asc",
                },
                {
                    headerName: this.$t("common.account.description"),
                    field: "tag",
                    minWidth: 200,
                    sortable: true,
                },
                {
                    headerName: this.$t("common.iban"),
                    field: "iban",
                    minWidth: 225,
                    sortable: true,
                },
                {
                    headerName: this.$t("common.bic"),
                    field: "bic",
                    minWidth: 150,
                    maxWidth: 150,
                    sortable: true,
                },
                {
                    headerName: this.$t("common.ccy"),
                    field: "currency",
                    minWidth: 75,
                    sortable: true,
                },
                {
                    headerName: this.$t("management.accounts.table.header.feeAccount"),
                    field: "isMainAccount",
                    minWidth: 150,
                    cellRendererFramework: "BooleanAsIcon",
                    sortable: true,
                },
                {
                    headerName: this.$t("management.accounts.table.header.portfolioAccount"),
                    field: "isBuyAccount",
                    minWidth: 150,
                    cellRendererFramework: "BooleanAsIcon",
                    sortable: true,
                },
                {
                    headerName: this.$t("common.status"),
                    field: "status",
                    minWidth: 150,
                    cellRendererFramework: "EnumAsText",
                    enumDef: enumDef.accountStatus,
                },
                {
                    headerName: this.$t("common.actions"),
                    minWidth: 100,
                    maxWidth: 150,
                    cellRendererFramework: "BankAccountsTableOperation",
                    cellRendererParams: {
                        editAccount: this.showEditAccountModal,
                        disableAccount: this.confirmDisableAccount,
                        enableAccount: this.confirmEnableAccount,
                        deleteAccount: this.confirmDeleteAccount
                    },
                    pinned: "right",
                }
            ],
        };
    },
    beforeMount() {
        this.getList();
    },
    methods: {
        sortChanged(sortData) {
            this.sorting = sortData;
            this.initPage();
            this.getList();
        },
        getList() {
            let postData = {
                columns: [],
                filters: this.filters || [],
                maxResults: this.maxResults,
                offset: this.offset,
                sorting: this.sorting
            };

            accountManagementApi.inspection.listAccounts(postData)
                .then(res => {
                    this.setTotalNum(res.recordsTotal);
                    this.rowData = res.data;
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        showAddAccountModal() {
            this.isDisplayAddAccountModal = true;
        },
        hideAddAccountModal() {
            this.isDisplayAddAccountModal = false;
        },
        showEditAccountModal(data) {
            this.editAccountData = data;
            this.isDisplayEditAccountModal = true;
        },
        hideEditAccountModal() {
            this.editAccountData = null;
            this.isDisplayEditAccountModal = false;
        },
        confirmDisableAccount(data) {
            let option = {
                title: this.$t("management.accounts.disable.confirm.title"),
                msg: this.$t("management.accounts.disable.confirm.msg"),
                confirmFun: this.disableAccount,
                params: data
            };
            this.confirmMeg(option, true);
        },
        disableAccount(data) {
            accountManagementApi.operation.disableAccount(data.id)
                .then(() => {
                    this.toastSuccess();
                    this.onAccountChanged();
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        confirmEnableAccount(data) {
            let option = {
                title: this.$t("management.accounts.enable.confirm.title"),
                msg: this.$t("management.accounts.enable.confirm.msg"),
                confirmFun: this.enableAccount,
                params: data
            };
            this.confirmMeg(option, true);
        },
        enableAccount(data) {
            accountManagementApi.operation.enableAccount(data.id)
                .then(() => {
                    this.toastSuccess();
                    this.onAccountChanged();
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        confirmDeleteAccount(data) {
            let option = {
                title: this.$t("management.accounts.delete.confirm.title"),
                msg: this.$t("management.accounts.delete.confirm.msg"),
                confirmFun: this.deleteAccount,
                params: data
            };
            this.confirmMeg(option, true);
        },
        deleteAccount(data) {
            accountManagementApi.operation.deleteAccount(data.id)
                .then(() => {
                    this.toastSuccess();
                    this.onAccountChanged();
                })
                .catch(e => {
                    this.toast(e);
                });
        },
        onAccountAdded() {
            this.hideAddAccountModal();
            this.getList();
        },
        onAccountChanged() {
            this.hideEditAccountModal();
            this.getList();
        },
        applyFilter(filters) {
            this.filters = filters;
            this.getList();
        },
    }
};
</script>